<script lang="ts" setup>

import { computed, ref, watch, onMounted } from 'vue'
import RoomieButtonComponent from '@lahaus-roomie/roomie/src/components/RoomieButton/index.vue'
import RoomieInputField from '@lahaus-roomie/roomie/src/components/RoomieInput/index.vue'
import RoomiePhoneInputField from '@lahaus-roomie/roomie/src/components/RoomiePhoneInput/index.vue'
import RoomieLoader from '@lahaus-roomie/roomie/src/components/RoomieLoader/index.vue'
import type { Props, PhoneProperties } from './types'
import OtpVerification from './OtpVerification.vue'
import { validateEmail } from '@/utils/validations/emailValidation'
import { SCREEN_CTA } from '@/utils/segment'
import { useContactFormStore } from '@/stores/contactFormStore'
import LegalPolicies from '@/components/LegalPolicies/index.vue'
import { useThemeStore } from '@/stores/useThemeStore'
import { loadCaptchaScript } from '@/components/App/LoginForm/utils'
import useRegisterForm from '@/composables/useRegisterForm'

const props = withDefaults(defineProps<Props>(), {
  listingIds: () => [],
  listingId: '',
  screen: '',
  totalSavings: 0,
  showEmailInput: true,
  showNameInput: true,
  showCtaIcon: false,
  attentionType: 'PRODUCT_BOT',
  showGeneralTitle: false,
  showSignInButton: true,
  shouldEnqueueTwilioContact: true,
  dataAttributes: () => ({})
})

const emit = defineEmits(['submit', 'sign-in'])

const themeStore = useThemeStore()
const { $cdpDataService, $config, $trackLead } = useNuxtApp()
const { t, locale } = useI18n()
const { $useLhId } = useNuxtApp()

const INPUT_STATUS = {
  default: 'default',
  success: 'success',
  error: 'error'
}

const legalPoliciesComponent = ref(null)
const otpVerificationRef = ref(null)
const emailValidated = ref(false)

const {
  form,
  status,
  externalError,
  legalPoliciesAccepted,
  showWarningLegalPolicy,
  setPhoneProperties,
  handlePhoneInputEvent,
  sendSlackAlert,
  createCustomer,
  sendOtpCode
} = useRegisterForm({
  listingIds: props.listingIds,
  listingId: props.listingId,
  screen: props.screen,
  totalSavings: props.totalSavings,
  hubCode: props.hubCode,
  monthlyPayments: props.monthlyPayments,
  attentionType: props.attentionType
})

const contactFormStore = useContactFormStore()

// Inicializamos el formulario con los datos del store
form.phone = contactFormStore.userPhone
form.email = contactFormStore.userEmail
form.name = contactFormStore.userName

const onboardingBedroomsNumber = useCookie<number>('onboarding_bedrooms_number')
const onboardingOpportunityType = useCookie<string>('onboarding_opportunity_type')

const title = computed(() => props.showGeneralTitle ? t('titleGeneral') : t('title'))

watch(() => contactFormStore.userEmail, (newValue) => {
  form.email = contactFormStore.userEmail
})

const emailStatus = computed(() => {
  if (!emailValidated.value) return INPUT_STATUS.default

  return validateEmail(form.email) ? INPUT_STATUS.success : INPUT_STATUS.error
})

const emailHelperText = computed(() => {
  if (emailStatus.value === INPUT_STATUS.success) return ''

  if (emailStatus.value === INPUT_STATUS.error) return form.email ? t('invalidEmail') : t('requiredEmail')

  return ''
})

const handleFormSubmit = async () => {
  emailValidated.value = true

  if (!legalPoliciesAccepted.value) {
    showWarningLegalPolicy.value = true
    return
  }

  legalPoliciesComponent.value?.getLegalPoliciesAcceptedProperties?.()

  showWarningLegalPolicy.value = false

  if (props.showEmailInput && emailStatus.value === INPUT_STATUS.error) return

  const isPhoneValid = form.phone_is_valid

  if (isPhoneValid) {
    try {
      contactFormStore.updateUserName(form.name)
      contactFormStore.updateUserEmail(form.email)
      contactFormStore.updateUserPhone(form.phone, false)
      contactFormStore.updateLegalConsent(legalPoliciesAccepted.value)

      const result = await createCustomer(
        $cdpDataService,
        $fetch,
        $trackLead,
        t,
        locale.value,
        onboardingBedroomsNumber.value,
        onboardingOpportunityType.value
      )

      if (result.success) {
        return await sendOtpCode($fetch, 'SMS', otpVerificationRef)
      } else if (result.error === 'existing_customer') {
        emit('sign-in', 'EXISTING_CUSTOMER_PHONE')
      } else {
        status.value = 'technical_error'
      }
    } catch (error) {
      useLogger().error({
        message: 'Error en el proceso de registro'
      })
      status.value = 'technical_error'
    }
  }
}

const handleSignInEvent = () => {
  emit('sign-in')
}

const roomieButtonDisabled = computed(() => {
  if (showWarningLegalPolicy.value) return true

  return !legalPoliciesAccepted.value
})

const siteKey = $config.public.googleRecaptchaSiteKey as string

onMounted(() => {
  loadCaptchaScript(siteKey)
})

const handleOtpSubmit = (userData: any) => {
  emit('submit', userData)
}

const handleOtpError = (title: string, data: any) => {
  status.value = 'technical_error'
  sendSlackAlert($fetch, title, data)
}
</script>

<script lang="ts">
export default {
  name: 'SyncRegisterForm'
}
</script>

<i18n src="./i18n.json" lang="json"></i18n>

<template>
  <div class="register-form">
    <div
      v-if="status==='form'">
      <h2
        v-sanitize.basic="title"
        class="hl-3-sb md:hl-2-sb my-8 text-pine-600" />

      <form
        class="mt-24 flex flex-col gap-y-16"
        @submit.prevent="handleFormSubmit">
        <RoomieInputField
          v-if="showNameInput"
          id="contact-form-name"
          v-model="form.name"
          :data-lh-id="$useLhId({ ...dataAttributes.dataLh, A: 'escribir', C: 'Escríbe tu nombre completo' })"
          :is-internal-validate="false"
          placeholder="Escríbe tu nombre completo"
          :required="true" />

        <RoomieInputField
          v-if="showEmailInput"
          id="contact-form-email"
          v-model="form.email"
          :helper-text="emailHelperText"
          :is-internal-validate="false"
          :status="emailStatus"
          :data-lh-id="$useLhId({ ...dataAttributes.dataLh, A: 'escribir', C: 'Tu correo' })"
          placeholder="Tu correo"
          :required="true"
          @blur="emailValidated = true" />

        <RoomiePhoneInputField
          id="lead-form-phone"
          class="-mt-20"
          :value="form.phone"
          :is-required="true"
          :initial-country="locale"
          number-label=""
          country-label=""
          name="lead-form-phone"
          :external-error="externalError"
          :tracking-lh-id="$useLhId({ ...dataAttributes.dataLh, A: 'escribir', C: 'Tu telefono' })"
          @full="(value: PhoneProperties) => setPhoneProperties(value)"
          @is-valid="(isValid: boolean) => form.phone_is_valid = isValid"
          @input="handlePhoneInputEvent" />

        <LegalPolicies
          ref="legalPoliciesComponent"
          v-model="legalPoliciesAccepted"
          class="!mb-0"
          :screen-cta="SCREEN_CTA.INVESTMENT_REGISTRATION_FORM"
          :screen="screen"
          :show-warning="showWarningLegalPolicy"
          :data-attributes="dataAttributes"
          :manual-event-handling="true"
          :phone="form.phone" />

        <RoomieButtonComponent
          :id="`${id}-submit-button`"
          :disabled="roomieButtonDisabled"
          :aria-label="t('cta')"
          type="submit"
          variant="primary"
          :data-lh-id="$useLhId({ ...dataAttributes.dataLh, A: 'enviar', C: t('cta') })"
          class="w-full gap-8 font-medium">
          <span class="flex">
            {{ t('cta') }}
          </span>
        </RoomieButtonComponent>
      </form>

      <div class="flex flex-col mt-24 gap-8">
        <p
          :class="{
            'body-1-sb text-pine-600': themeStore.isV2,
            'text-carbon-800 font-medium': !themeStore.isV2
          }"
          class="text-center">
          {{ t('signInQuestion') }}

          <a
            :id="`${id}-authentication-button`"
            v-lh-id="{ ...dataAttributes.dataLh, A: 'redireccionar', C: t('signIn') }"
            :class="{
              'body-2-sb text-pine-600': themeStore.isV2,
              'font-medium text-primary-800': !themeStore.isV2
            }"
            class="ml-8 gap-8 underline"
            href="#"
            :aria-label="t('signIn')"
            @click="handleSignInEvent">
            {{ t('signIn') }}
          </a>
        </p>
      </div>
    </div>

    <div
      v-else-if="status === 'technical_error'"
      id="technical-error-form"
      class="h-[314px] flex flex-col items-center">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="58"
        height="88"
        viewBox="0 0 58 58"
        fill="none">
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M29.0001 57.3627C25.7392 57.3627 22.6484 55.945 20.5076 53.4922C19.8271 52.6982 18.7496 52.2729 17.7004 52.3296C14.4395 52.5423 11.2495 51.3655 8.95271 49.0546C6.65591 46.7578 5.46498 43.5678 5.67764 40.3069C5.74853 39.2719 5.30902 38.1944 4.51506 37.4997C2.04813 35.3588 0.644531 32.2539 0.644531 28.993C0.644531 25.7321 2.06231 22.6414 4.51506 20.5005C5.30902 19.82 5.74853 18.7425 5.67764 17.6933C5.4508 14.4324 6.64173 11.2424 8.95271 8.94563C11.2495 6.64883 14.4679 5.44372 17.7004 5.67056C18.7354 5.72727 19.8129 5.30194 20.5076 4.50798C22.6484 2.04105 25.7534 0.637451 29.0001 0.637451C32.2468 0.637451 35.3517 2.05523 37.4926 4.50798C38.1731 5.30194 39.2506 5.74145 40.2998 5.67056C43.5465 5.44372 46.7507 6.63465 49.0475 8.94563C51.3443 11.2424 52.5352 14.4324 52.3225 17.6933C52.2516 18.7425 52.6912 19.8058 53.4851 20.5005C55.952 22.6414 57.3556 25.7463 57.3556 28.993C57.3556 32.2397 55.9379 35.3447 53.4851 37.4855C52.6912 38.166 52.2516 39.2435 52.3225 40.2927C52.5494 43.5536 51.3584 46.7436 49.0475 49.0404C46.7507 51.3372 43.5607 52.5281 40.2998 52.3154C39.2506 52.2446 38.1873 52.6841 37.4926 53.478C35.3517 55.945 32.2468 57.3486 29.0001 57.3486V57.3627ZM25.9841 13.3139H32.0172V33.8264H25.9841V13.3139ZM32.0172 38.6528H25.9841V44.6859H32.0172V38.6528Z"
          fill="#6400B9" />
      </svg>

      <div class="flex flex-col items-center justify-center">
        <p
          v-sanitize="t('technicalErrorTitle')"
          class="hl-2-sb md:display-6 text-pine-600 text-24 mb-24 text-center" />

        <p class="body-2-m md:body-1-m text-pine-600 mb-24 text-center">
          {{ t('technicalErrorDescription') }}
        </p>

        <RoomieButtonComponent
          :id="`${id}-error-start-button`"
          class="w-full gap-8 font-medium"
          :data-lh-id="$useLhId({ ...dataAttributes.dataLh, A: 'redireccionar', C: t('technicalErrorCta') })"
          type="submit"
          variant="primary"
          :aria-label="t('technicalErrorCta')"
          @click="status = 'form'">
          <span class="flex">
            {{ t('technicalErrorCta') }}
          </span>
        </RoomieButtonComponent>
      </div>
    </div>

    <OtpVerification
      v-else-if="status === 'otp'"
      ref="otpVerificationRef"
      :phone="form.phone"
      :phone-prefix="form.phone_prefix"
      :phone-number="form.phone_number"
      :name="form.name"
      :email="form.email"
      :site-key="siteKey"
      :data-attributes="dataAttributes"
      @submit="handleOtpSubmit"
      @back="status = 'form'"
      @error="handleOtpError" />

    <div
      v-else
      class="h-[337px] flex items-center justify-center">
      <RoomieLoader
        size="xl"
        color="primary" />
    </div>
  </div>
</template>

<style lang="scss">
.lock-overlay__registration-modal {
  .roomie-modal__body {
    @apply mt-16;
  }
}

.register-form {
  .roomie-button.roomie-button--filled {
    &:disabled {
      @apply bg-carbon-light-50 text-carbon-light-600 opacity-50;

      border: 1px solid #919E9B;

      svg {
        @apply fill-carbon-light-600;
      }
    }
  }
}

.divider-text {
  @apply flex items-center w-full gap-16;

  p {
    @apply whitespace-nowrap;
  }

  &::before,
  &::after {
    @apply inline-block bg-carbon-light-300;

    content: '';
    width: 100%;
    height: 1px;
  }
}
</style>
